
/**
 * External dependencies.
 */
import Component, { mixins } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

/**
 * Internal dependencies.
 */
import IsotopeMixin from '@/mixins/IsotopeMixin.vue'
import { ImageData } from '@/store/voting/interfaces/ImageData'
import { namespace as profileModule } from '@/store/profile'
import { SET_GALLERY_STATE } from '@/store/profile/constants'

@Component
export default class IsotopePhotos extends mixins(IsotopeMixin) {
  @Prop({ type: Array, required: true }) photosToRender!: Array<ImageData> | object
  @Prop(Boolean) isLoading!: Boolean

  photos: any = []
  page: number = 1
  isFirefox: boolean = false
  cacheIndex: any = []
  isReady: boolean = false
  isInitialRender: boolean = true
  isIsotopeInitialized: boolean = false
  isRenderingImages: boolean = false

  @profileModule.Getter isGalleryReady: boolean
  @profileModule.Action(SET_GALLERY_STATE) setGalleryLoadingState: (value: boolean) => Promise<any>

  @Watch('photosToRender', { deep: true })
  onFavsPropChange (value: object) {
    void this.renderImages(value)
  }

  @Watch('isLoading', { deep: true })
  onLoadingChange (value: object) {
    if (value) {
      this.isReady = false
      void this.setGalleryLoadingState(true)
    }
  }

  mounted () {
    void this.setGalleryLoadingState(true)
    void this.renderImages(this.photosToRender)
  }

  // method to batch load images
  async renderImages (photos: any) {
    // page is loading with initial state, dont render
    if (this.isInitialRender) {
      this.isInitialRender = false
      return
    }

    // api returned empty photo set, dont render, disable loading
    if (photos.length === 0) {
      this.isReady = true
      void this.setGalleryLoadingState(false)
      return
    }

    this.isRenderingImages = true
    this.$emit('rendering-images')

    const imagesLoadedPromises: Promise<any>[] = []

    photos.forEach((photo: ImageData) => {
      const loadImg = new Image()
      const loadingPromise = new Promise((resolve) => {
        loadImg.onload = resolve
        loadImg.onerror = resolve
      })

      imagesLoadedPromises.push(loadingPromise)

      let imageSource = photo.urls.small

      // Firefox specific fix: for cache related bug
      if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
        this.isFirefox = true

        // maybe change randomization implementation later
        let randoString = Math.random().toString(36).substr(2, 5)
        // let randoNum = Math.floor(Math.random() * 1000)
        this.cacheIndex.push(randoString)

        imageSource = `${photo.urls.small}?cache=${randoString}`
      }

      loadImg.src = imageSource
    })

    // wait for all images to load
    await Promise.all(imagesLoadedPromises)

    // append the photos from the function
    // as we are only receiving batches and need to append them to local photos datz
    this.photos = [...this.photos, ...photos]

    await this.$nextTick()

    try {
      // check if ->initial page load
      await (!this.isIsotopeInitialized ? this.initLayout() : this.relayoutGrid(this.page))

      this.page++
      this.isReady = true
      void this.setGalleryLoadingState(false)

      this.isRenderingImages = false
      this.$emit('stopped-rendering-images')
    } catch {
      // empty catch
    } finally {
      this.isIsotopeInitialized = true
    }
  }

  render () {
    const { page, photos, isFirefox, cacheIndex, isotopeWidth, isReady, isRenderingImages } = this

    return this.$scopedSlots && this.$scopedSlots.default!({
      page,
      photos,
      isFirefox,
      cacheIndex,
      isotopeWidth,
      isReady,
      isRenderingImages
    })
  }
}
